import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/App';
import { chatAPI, messageAPI } from '@/lib/api';
import wsService from '@/lib/websocket';
import webRTCService from '@/lib/webrtc';
import ChatSidebar from '@/components/ChatSidebar';
import ChatView from '@/components/ChatView';
import EmptyChat from '@/components/EmptyChat';
import CallModal from '@/components/CallModal';
import IncomingCallDialog from '@/components/IncomingCallDialog';
import SettingsPage from '@/components/SettingsPage';
import AdminDashboard from '@/components/AdminDashboard';
import { toast } from 'sonner';
import axios from 'axios';

// --- Hilfsfunktion für Push ---
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function ChatPage({ isDark, setIsDark }) {
  const { user, token, logout, updateUser } = useAuth();
  
  // --- STATES ---
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSidebar, setShowSidebar] = useState(true);
  
  // Status States
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  
  // Call Protection State
  const [isProcessingCall, setIsProcessingCall] = useState(false);
  
  const activeChatRef = useRef(null);
  
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Page views
  const [showSettings, setShowSettings] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  
  // Call states
  const [isInCall, setIsInCall] = useState(false);
  const [isCallInitiator, setIsCallInitiator] = useState(false);
  const [callType, setCallType] = useState(null);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [incomingCallInfo, setIncomingCallInfo] = useState(null);
  const [callTargetUser, setCallTargetUser] = useState(null);
  const [callChatId, setCallChatId] = useState(null);
  
  // Ref für Calls (speichert das Offer sicher)
  const pendingOfferRef = useRef(null);

  // Push-Funktion
  const subscribeToPush = async () => {
    if (user?.notifications_enabled === false) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    
    try {
        const registration = await navigator.serviceWorker.ready;

        if (Notification.permission === 'default') {
            await Notification.requestPermission();
        }
        
        if (Notification.permission !== 'granted') return;

        const publicVapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        if (!publicVapidKey) return;

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicVapidKey)
            });
        }

        await axios.post('/api/push/subscribe', subscription, {
            headers: { Authorization: `Bearer ${token}` }
        });

    } catch (error) {
        console.error("Push Error:", error);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setShowSidebar(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadChats = useCallback(async () => {
    try {
      const response = await chatAPI.getChats();
      setChats(response.data);
    } catch (error) {
      console.error('Failed to load chats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChats();
    const timeout = setTimeout(subscribeToPush, 3000);
    return () => clearTimeout(timeout);
  }, [loadChats]);

  const handleInteraction = () => {
     subscribeToPush();
  };

  const loadMessages = useCallback(async (chatId) => {
    try {
      const response = await messageAPI.getMessages(chatId);
      setMessages(response.data);
      await messageAPI.markAsRead(chatId);
      setChats(prev => prev.map(c => 
        c.id === chatId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }, []);

  // --- WEBSOCKET & CALL HANDLING LOGIC ---
  useEffect(() => {
    if (token) {
      wsService.connect(token);

      // 1. Standard Chat Handlers
      const handleAddedToGroup = (data) => {
        const newChat = data.chat || data; 
        if (!newChat || !newChat.id) return;

        setChats(prev => {
            if (prev.find(c => c.id === newChat.id)) return prev;
            return [newChat, ...prev];
        });
        
        const displayName = newChat.name || "einem neuen Chat";
        toast.info(`Du wurdest zu "${displayName}" hinzugefügt`);
        playNotificationSound();

        if (wsService.socket && wsService.socket.connected) {
              wsService.send({ type: 'join', chat_id: newChat.id });
        }
      };

      const handleNewMessage = (data) => {
        const msg = data.message;
        
        if (activeChatRef.current?.id === msg.chat_id) {
          setMessages(prev => {
             if (msg.reply_to_id && !msg.reply_to_message) {
                 const originalMsg = prev.find(m => m.id === msg.reply_to_id);
                 if (originalMsg) {
                     msg.reply_to_message = {
                          sender_username: originalMsg.sender_username,
                          content: originalMsg.content,
                          type: originalMsg.type
                      };
                 }
             }
             return [...prev, msg];
          });
          messageAPI.markAsRead(msg.chat_id);
        } else {
          if (msg.sender_id !== user?.id) {
            toast(`Neu von ${msg.sender_username}`, {
              description: msg.type === 'image' ? '📷 Bild' : msg.content,
            });
            playNotificationSound();
          }
        }

        setChats(prev => {
          const chatIndex = prev.findIndex(c => c.id === msg.chat_id);
          if (chatIndex === -1) {
            loadChats();
            return prev;
          }
          const updated = [...prev];
          const chat = { ...updated[chatIndex] };
          chat.last_message = msg;
          if (activeChatRef.current?.id !== msg.chat_id && msg.sender_id !== user?.id) {
            chat.unread_count = (chat.unread_count || 0) + 1;
          }
          updated.splice(chatIndex, 1);
          updated.unshift(chat);
          return updated;
        });
      };

      const handleTyping = (data) => {
        if (data.user_id !== user?.id) {
          setTypingUsers(prev => ({
            ...prev,
            [data.chat_id]: data.is_typing ? data.user_id : null
          }));
          if (data.is_typing) {
            setTimeout(() => {
              setTypingUsers(prev => ({
                ...prev,
                [data.chat_id]: null
              }));
            }, 3000);
          }
        }
      };

      const handleStatus = (data) => {
        setOnlineUsers(prev => ({
          ...prev,
          [data.user_id]: data.is_online
        }));
        setChats(prev => prev.map(chat => ({
          ...chat,
          participants: chat.participants?.map(p => 
            p.id === data.user_id ? { ...p, is_online: data.is_online } : p
          )
        })));
      };

      const handleReadReceipt = (data) => {
        if (activeChatRef.current?.id === data.chat_id) {
          setMessages(prev => prev.map(msg => ({
            ...msg,
            read_by: msg.read_by.includes(data.user_id) 
              ? msg.read_by 
              : [...msg.read_by, data.user_id]
          })));
        }
      };
      
      const handleReaction = (data) => {
         setMessages(prev => {
             const exists = prev.some(m => m.id === data.message_id);
             if (!exists) return prev;
             return prev.map(msg => {
                if (msg.id === data.message_id) {
                    return { ...msg, reactions: data.reactions };
                }
                return msg;
             });
         });
      };

      const handleMessageEdited = (data) => {
         const updatedMsg = data.message;
         if (activeChatRef.current?.id === updatedMsg.chat_id) {
            setMessages(prev => prev.map(msg => 
               msg.id === updatedMsg.id ? updatedMsg : msg
            ));
         }
         setChats(prev => prev.map(c => {
             if (c.id === updatedMsg.chat_id && c.last_message?.id === updatedMsg.id) {
                 return { ...c, last_message: updatedMsg };
             }
             return c;
         }));
      };

      const handleMessageDeleted = (data) => {
         if (data.for_everyone) {
            setMessages(prev => prev.filter(msg => msg.id !== data.message_id));
            loadChats();
         }
      };

      // 2. WebRTC Call Handlers
      const handleCallOffer = (data) => {
        //console.log("📥 SOCKET: Call Offer empfangen!", data);
        
        pendingOfferRef.current = data.offer;
        
        setIsCallInitiator(false);
        setIncomingCallInfo({
          id: data.from_user_id,
          username: data.from_username,
          avatar: data.from_avatar,
          offer: data.offer 
        });
        setCallType(data.call_type);
        setCallChatId(data.chat_id);
        setIsIncomingCall(true);
        playNotificationSound();
      };

      const handleCallAnswer = async (data) => {
        //console.log("📥 SOCKET: Call Answer empfangen!", data);
        try {
          await webRTCService.handleAnswer(data.answer);
          //console.log("✅ Answer erfolgreich verarbeitet");
        } catch (error) {
          console.error("❌ Fehler bei handleAnswer:", error);
        }
      };

      const handleIceCandidate = async (data) => {
        //console.log("📥 SOCKET: ICE Candidate empfangen", data.candidate ? "OK" : "LEER");
        if (data.candidate) {
             await webRTCService.addIceCandidate(data.candidate);
        }
      };

      const handleCallEnd = (data) => {
        //console.log('🚫 Call ended:', data.reason);
        // Aufräumen (hier lokal definiert, da wir nicht auf handleEndCall zugreifen können)
        webRTCService.endCall();
        setIsInCall(false);
        setIsIncomingCall(false);
        setIncomingCallInfo(null);
        
        if (data.reason === 'rejected') toast.error('Anruf abgelehnt');
        else if (data.reason === 'busy') toast.error('Benutzer ist beschäftigt');
      };

      // 3. Listener Registrierung
      wsService.on('new_message', handleNewMessage);
      wsService.on('added_to_group', handleAddedToGroup);
      wsService.on('new_chat', handleAddedToGroup);
      wsService.on('chat_updated', handleAddedToGroup);
      
      wsService.on('typing', handleTyping);
      wsService.on('status', handleStatus);
      wsService.on('read_receipt', handleReadReceipt);
      wsService.on('message_reaction', handleReaction);
      wsService.on('message_edited', handleMessageEdited);
      wsService.on('message_deleted', handleMessageDeleted);
      
      wsService.on('call_offer', handleCallOffer);
      wsService.on('call_answer', handleCallAnswer);
      wsService.on('ice_candidate', handleIceCandidate);
      wsService.on('call_end', handleCallEnd);

      return () => {
        wsService.off('new_message', handleNewMessage);
        wsService.off('added_to_group', handleAddedToGroup);
        wsService.off('new_chat', handleAddedToGroup);
        wsService.off('chat_updated', handleAddedToGroup);

        wsService.off('typing', handleTyping);
        wsService.off('status', handleStatus);
        wsService.off('read_receipt', handleReadReceipt);
        wsService.off('message_reaction', handleReaction);
        wsService.off('message_edited', handleMessageEdited);
        wsService.off('message_deleted', handleMessageDeleted);
        
        wsService.off('call_offer', handleCallOffer);
        wsService.off('call_answer', handleCallAnswer);
        wsService.off('ice_candidate', handleIceCandidate);
        wsService.off('call_end', handleCallEnd);
        wsService.disconnect();
      };
    }
  }, [token, user?.id, loadChats]);

  // Initial load
  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // Load messages when chat changes
  useEffect(() => {
    if (activeChat) {
      loadMessages(activeChat.id);
      if (isMobile) setShowSidebar(false);
    }
  }, [activeChat, loadMessages, isMobile]);

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQxBjdXOi1IBEhRQaX2cqJNkJxEwYojB4tCaWioUGE5siam1p4BPKBgtW4a44NawZTAAGEx3lrK3sYRWMyAoW4a33NeoZTUAGE14lrK3sYRXMyAnW4a33NeoZTUAGEx4lrK3sYNWMyEnW4a33NeoZjQAGUx4lrO3sIRWMyEnXIa33deoZjQAGEx4lrK3sYNWMyEnW4a33NeoZjQBGEx4lrO3sINWNCEnW4a33deoZjQAGE14lrK3sYNWNCEnW4a33NenZjQAGEx4lbO3sYNWMyAnXIa33daoZjUAGE14lrK3sYRWMx8nW4a33deoZjQAGEx4lbK3sYRWMyAnXIa33deoZjQAGE14lrK4sIRWMx8nW4a33deoZjQAGE14lrO3sYRWMx8nW4a33deoZjQBGEx4lbK3sYNWNCAnW4a33deoZjQAGU14lrK3sYNWMyEnW4a33NeoZjQAGU14lbK3sYNWMyEnW4a33deoZjQAGE14lrK3sYNWMyAnW4a33NeoZjQAGE14lrK3sYNWMyEnW4a33NeoZjQAGE14lrK3sYNWMyEnW4a33NeoZjQAGE14lrK3sYNWMiEnW4a33NeoZjQAGEx4lrK3sYNWMyEnW4a33NaoZjQAGE14lrK3sYNWMiEnW4a33NeoZjQAGE14lrK3sYNWMyEnW4a33NeoZjQAGE14lrK3sYNWMyEnW4a33NeoZjQAGE14lrK3sYNWMyEnW4a33NeoZjQAGE14lrK3sYNWMyEnW4a33NeoZjQ');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  };

  const handleSelectChat = (chat) => {
    setActiveChat(chat);
  };

  const handleNewChat = (chat) => {
    setChats(prev => {
      const exists = prev.find(c => c.id === chat.id);
      if (exists) return prev;
      return [chat, ...prev];
    });
    setActiveChat(chat);
  };

  const handleBack = () => {
    setShowSidebar(true);
    if (isMobile) setActiveChat(null);
  };

  const handleSendMessage = async (content, type = 'text', fileName = null, replyTo = null) => {
    if (!activeChat) return;
    try {
      await messageAPI.sendMessage({
        chat_id: activeChat.id,
        content,
        type,
        file_name: fileName,
        reply_to: replyTo
      });
    } catch (error) {
      toast.error('Nachricht konnte nicht gesendet werden');
    }
  };

  // Start outgoing call
  const handleStartCall = (type, targetUser) => {
    if (!targetUser) {
      toast.error('Kein Benutzer zum Anrufen');
      return;
    }
    
    setIsCallInitiator(true); 

    setCallType(type);
    setCallTargetUser(targetUser);
    setCallChatId(activeChat?.id);
    setIsIncomingCall(false);
    setIsInCall(true);
    toast.info(`${type === 'video' ? 'Video' : 'Audio'}anruf wird gestartet...`);
  };

  // --- HILFSFUNKTION FÜR CLEANUP ---
  const handleEndCallCleanup = () => {
    webRTCService.endCall();
    setIsInCall(false);
    setIsIncomingCall(false);
    setCallTargetUser(null);
    setIncomingCallInfo(null);
    setIsCallInitiator(false);
    pendingOfferRef.current = null;
  };

  // --- ANRUF ANNEHMEN (ROBUST) ---
  const handleAcceptCall = async () => {
    if (isProcessingCall) return; 
    setIsProcessingCall(true);
    //console.log("📞 ANNEHMEN GEKLICKT - Starte Prozess...");

    try {
      // 1. UI Status sofort updaten
      setIsIncomingCall(false);
      setCallTargetUser(incomingCallInfo);
      
      const offerToProcess = pendingOfferRef.current || incomingCallInfo?.offer;
      
      if (!offerToProcess) {
        console.error("❌ KRITISCH: Kein Offer vorhanden! Abbruch.");
        toast.error("Verbindungsfehler: Anrufdaten fehlen.");
        handleEndCallCleanup();
        return;
      }

      //console.log("1️⃣ Initialisiere Medien...");
      await webRTCService.initializeCall(callType); 
      
      setIsInCall(true); 

      // Audio-Fix für Mobile
      if (typeof webRTCService?.resumeAudio === "function") webRTCService.resumeAudio();

      // Callback setzen BEVOR wir die Answer senden!
      webRTCService.onIceCandidate = (candidate) => {
        //console.log("📤 Sende eigenen ICE Candidate");
        wsService.send({
          type: 'ice_candidate',
          target_user_id: incomingCallInfo.id,
          candidate
        });
      };

      //console.log("2️⃣ Verarbeite Offer (SetRemoteDescription)...");
      const answer = await webRTCService.handleOffer(offerToProcess);
      //console.log("✅ Offer verarbeitet, Answer erstellt");
      
      //console.log("3️⃣ Sende Answer...");
      wsService.send({
        type: 'call_answer',
        target_user_id: incomingCallInfo.id,
        answer
      });
      
      pendingOfferRef.current = null;

    } catch (error) {
      console.error('❌ FEHLER beim Annehmen:', error);
      toast.error('Verbindung fehlgeschlagen');
      handleEndCallCleanup();
    } finally {
      setIsProcessingCall(false);
    }
  };

  // --- ANRUF ABLEHNEN ---
  const handleRejectCall = () => {
    //console.log("🚫 Anruf abgelehnt");
    if (incomingCallInfo?.id) {
      wsService.send({
        type: 'call_end',
        target_user_id: incomingCallInfo.id,
        reason: 'rejected'
      });
    }
    handleEndCallCleanup();
    setIsProcessingCall(false);
  };

  // --- ANRUF BEENDEN (NORMAL) ---
  const handleEndCall = () => {
    const targetId = isIncomingCall ? incomingCallInfo?.id : callTargetUser?.id;
    if (targetId) {
      wsService.send({
        type: 'call_end',
        target_user_id: targetId,
        reason: 'ended'
      });
    }
    handleEndCallCleanup();
  };

  if (showSettings) {
    return (
      <SettingsPage
        user={user}
        onClose={() => setShowSettings(false)}
        onUserUpdate={updateUser}
        isDark={isDark}
        setIsDark={setIsDark}
      />
    );
  }

  if (showAdmin && user?.is_superadmin) {
    return (
      <AdminDashboard
        onClose={() => setShowAdmin(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div 
      className="h-full w-full flex bg-slate-50 dark:bg-slate-950" 
      data-testid="chat-page"
      onClick={handleInteraction}
      onTouchStart={handleInteraction}
    >
      <AnimatePresence mode="wait">
        {(!isMobile || showSidebar) && (
          <motion.div
            initial={isMobile ? { x: -300 } : false}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={`${isMobile ? 'absolute inset-0 z-10' : 'w-[350px] border-r border-slate-200 dark:border-slate-800'} bg-white dark:bg-slate-900 flex flex-col`}
          >
            <ChatSidebar
              chats={chats}
              activeChat={activeChat}
              user={user}
              onSelectChat={handleSelectChat}
              onNewChat={handleNewChat}
              onLogout={logout}
              isDark={isDark}
              setIsDark={setIsDark}
              typingUsers={typingUsers}
              onOpenSettings={() => setShowSettings(true)}
              onOpenAdmin={() => setShowAdmin(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`flex-1 flex flex-col ${isMobile && showSidebar ? 'hidden' : ''}`}>
        {activeChat ? (
          <ChatView
            chat={activeChat}
            messages={messages}
            user={user}
            onSendMessage={handleSendMessage}
            onBack={handleBack}
            isMobile={isMobile}
            typingUserId={typingUsers[activeChat.id]}
            onStartCall={handleStartCall}
            onMessagesUpdate={() => loadMessages(activeChat.id)}
            allChats={chats}
            onChatUpdate={loadChats}
          />
        ) : (
          <EmptyChat />
        )}
      </div>

      <IncomingCallDialog
        isOpen={isIncomingCall && !isInCall}
        callerInfo={incomingCallInfo}
        callType={callType}
        onAccept={handleAcceptCall}
        onReject={handleRejectCall}
      />

      <CallModal
        isOpen={isInCall}
        onClose={handleEndCall}
        callType={callType}
        isIncoming={!isCallInitiator}
        callerInfo={incomingCallInfo}
        targetUser={callTargetUser}
        chatId={callChatId}
        currentUserId={user?.id}
      />
    </div>
  );
}